"use strict";
import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment-timezone";
import { Button, Grid, Icon, Select, Text } from "@citifyd/style";

import {
  AdminGetAccountPermissionCompaniesQuery,
  AdminGetAccountPermissionCompaniesQueryVariables,
  useAdminDeleteAccountPermissionCompanyMutation,
} from "../../../graphql/generated/graphql";

import ListToSeeMore from "../../../shared/react/components/ListToSeeMore";
import SearchInput from "../../../shared/react/components/SearchInput";
import Table from "../../../shared/react/components/Table";
import { Pagination } from "../Pagination/Pagination";

import ApolloClient from "../../../shared/services/apolloClient";
import { useTranslator } from "../../../shared/react/hooks";
import { usePagination } from "../Pagination/usePagination";
import { getService } from "../../../shared/react/utils";
import { useAuthentication } from "../../../shared/react/contexts/authentication";

import { formatPhoneNumber } from "../../../shared/utils/phone-number-formatting";
import { LotOption } from "./types";

import styles from "../AccountPermissions.module.scss";

import { ColumnProps } from "../../../shared/react/components/Table/Table";
interface PermissionCompaniesProps {
  loading: boolean;
  lotsOptions: LotOption[];
  pageSize: number;
  permissionCompanies?: AdminGetAccountPermissionCompaniesQuery["getAccountPermissionCompanies"];
  refetch: (variables: AdminGetAccountPermissionCompaniesQueryVariables) => any;
  setDebouncedSearch: (value: string) => void;
  selectedLots: LotOption[];
  setSelectedLots: (value: LotOption[]) => void;
}

const apolloClient = ApolloClient();

export const ActivePermissionCompanies = ({
  loading,
  lotsOptions,
  pageSize,
  permissionCompanies,
  refetch,
  setDebouncedSearch,
  selectedLots,
  setSelectedLots,
}: PermissionCompaniesProps) => {
  const t = useTranslator();
  const searchTimerRef = useRef<NodeJS.Timer>();
  const [search, setSearch] = useState("");
  const { user } = useAuthentication();
  const { hasNextPage, hasPreviousPage, nextPage, previousPage } =
    usePagination({
      data: permissionCompanies,
      pageSize,
      refetch,
    });

  const [deletePermissionCompany] =
    useAdminDeleteAccountPermissionCompanyMutation({
      client: apolloClient,
    });

  const deleteCompany = async ({ userId }: { userId: number }) => {
    if (window.confirm(t("commonConfirmations.areYouSure"))) {
      await deletePermissionCompany({
        variables: {
          organizationId: user.organization.id,
          userId,
        },
      });
      refetch({
        first: pageSize,
        last: null,
        after: null,
        before: null,
      });
    }
  };

  const columns: ColumnProps[] = [
    {
      value: t("accountPermissions.permissionCompanies.name"),
      uppercase: false,
      key: "name",
      width: "20%",
    },
    {
      value: t("accountPermissions.permissionCompanies.phone"),
      uppercase: false,
      key: "phoneNumber",
      width: "25%",
    },

    {
      value: t("accountPermissions.permissionCompanies.properties"),
      uppercase: false,
      key: "properties",
      width: "25%",
    },
    {
      value: t("accountPermissions.permissionCompanies.lastLogin"),
      uppercase: false,
      key: "lastLoginAt",
      width: "20%",
    },

    {
      value: t("accountPermissions.actions"),
      uppercase: false,
      key: "actions",
      width: "10%",
      textAlign: "right",
    },
  ];

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const editCompany = (id) => {
    const $state = getService("$state");

    $state.go(
      "attendant-monitoring-company-edit",
      { id },
      { enableBackLink: true }
    );
  };

  useEffect(() => {
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
  }, [search]);

  const data = useMemo(
    () =>
      permissionCompanies?.edges.map((edge) => {
        const propertyNames = edge.node.properties?.map(
          (property) => property?.name
        );

        return {
          ...edge.node,
          name: {
            value: edge.node.name,
          },
          phoneNumber: {
            value: formatPhoneNumber(
              edge.node.phoneNumber,
              edge.node.phoneCountryCode
            ),
          },
          properties: {
            value: (
              <ListToSeeMore
                tooltipProps={{ place: "right" }}
                items={propertyNames}
                limit={1}
              />
            ),
          },
          lastLoginAt: {
            value: moment(edge.node.lastLoginAt).format("lll"),
          },
          actions: {
            textAlign: "right",
            value: (
              <>
                <Button
                  justifyContent="center"
                  appearance="transparent"
                  onClick={() => editCompany(edge.node.id)}
                >
                  <Icon icon="pencil-alt" />
                </Button>
                <Button
                  justifyContent="center"
                  appearance="transparent"
                  onClick={() => {
                    deleteCompany({ userId: edge.node.id });
                  }}
                >
                  <Icon icon="trash" />
                </Button>
              </>
            ),
          },
        };
      }),
    [permissionCompanies?.edges]
  );

  return (
    <>
      <Grid.Row>
        <Grid.Col md={3}>
          <Select
            allItemsOption={t("commonTexts.allProperties")}
            allItemsLabel={t("commonTexts.allProperties")}
            fullWidth
            gutterBottom
            multiple
            options={lotsOptions}
            value={selectedLots}
            isOptionDisabled={() => false}
            onChange={(e) => {
              setSelectedLots(e as LotOption[]);
            }}
          />
        </Grid.Col>

        <Grid.Col md={3} offset={{ md: 6 }}>
          <SearchInput
            gutterBottom
            fullWidth
            onChange={(e) => handleSearchChange(e)}
          />
        </Grid.Col>
      </Grid.Row>

      <div className={styles.container}>
        {data && (
          <>
            <Text weight="300" variant="h5" gutterBottom>
              {t(
                "accountPermissions.permissionCompanies.permissionCompaniesDescripition"
              )}
            </Text>

            <Table
              headerAppearance="transparent"
              spacing
              columns={columns}
              data={data}
              responsive
            />
            {(hasNextPage || hasPreviousPage) && (
              <Pagination
                loading={loading}
                hasNextPage={hasNextPage}
                hasPreviousPage={hasPreviousPage}
                nextPage={nextPage}
                previousPage={previousPage}
                data={permissionCompanies?.edges}
              />
            )}
          </>
        )}
      </div>
    </>
  );
};
